import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FiFileText,
  FiBell,
  FiLock,
  FiPhoneCall,
  FiCheckSquare,
} from "react-icons/fi";
import MonthlyReport from "./UserMonthlyReport";
import { useNavigate, useParams } from "react-router-dom";
import "./RMDashboard.css";
import api from "../api/axiosConfig";

import SalesTracker from "./SalesTracker";
import ProfilePage from "./ProfilePage";
import {
  FiBarChart2,
  FiCheckCircle,
  FiClock,
  FiPlus,
  FiAward,
  FiTarget,
  FiUsers,
  FiTrendingUp,
  FiSettings,
} from "react-icons/fi";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Snackbar,
  Tooltip,
} from "@mui/material";
import logo1 from "../images/ACMlogobgremove.png";
import defaultAvatar from "../images/download.png";

const toTitleCase = (str) => {
  if (!str) return "";
  const withSpaces = str.replace(/_/g, " ").replace(/([A-Z])/g, " $1");
  return withSpaces.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

const formatDateToYMD = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isIndianGovernmentHoliday = (date, corporateHolidays = []) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Check against corporate holidays from API
  const holidayFromAPI = corporateHolidays.find(holiday => {
    const holidayDate = new Date(holiday.date);
    return holidayDate.getFullYear() === year &&
           holidayDate.getMonth() === month &&
           holidayDate.getDate() === day;
  });

  if (holidayFromAPI) {
    return {
      name: holidayFromAPI.name,
      type: holidayFromAPI.type
    };
  }

  // Fallback to basic fixed holidays if API fails (for redundancy)
  const fixedHolidays = [
    { month: 0, day: 1, name: "New Year's Day" },
    { month: 0, day: 26, name: "Republic Day" },
    { month: 7, day: 15, name: "Independence Day" },
    { month: 9, day: 2, name: "Gandhi Jayanti" },
    { month: 11, day: 25, name: "Christmas" },
    { month: 4, day: 1, name: "Labour Day" },
  ];

  const fixedHoliday = fixedHolidays.find(holiday => holiday.month === month && holiday.day === day);
  if (fixedHoliday) {
    return {
      name: fixedHoliday.name,
      type: 'national'
    };
  }

  return null;
};

const RMDashboard = () => {
  const navigate = useNavigate();
  const { section, date: dateFromUrl } = useParams();
  const currentSection = section || "dashboard";

  const [isLoading, setIsLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(defaultAvatar);
  const [dashboardData, setDashboardData] = useState(null);
  // <<< STEP 1: ADD NEW STATE FOR STATIC MONTHLY KPI DATA >>>
  const [monthlyKpiData, setMonthlyKpiData] = useState({
    kpis: {},
    previousMonthKpis: {},
  });
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [isOnLeave, setIsOnLeave] = useState(false);
  const isInitialMount = useRef(true);
  const [userName] = useState(localStorage.getItem("userName") || "User");
  const userRole = localStorage.getItem("userRole");

  const today = new Date();
  const firstDayOfCurrentMonth = new Date(
    today.getFullYear(),
    today.getMonth(),
    1
  );
  const lastDayOfCurrentMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0
  );

  const [chartStartDate, setChartStartDate] = useState(firstDayOfCurrentMonth);
  const [chartEndDate, setChartEndDate] = useState(lastDayOfCurrentMonth);

  const [calendarStartDate, setCalendarStartDate] = useState(
    firstDayOfCurrentMonth
  );
  const [calendarEndDate, setCalendarEndDate] = useState(lastDayOfCurrentMonth);

  const [selectedField1, setSelectedField1] = useState("dailyPoints");
  const [selectedField2, setSelectedField2] = useState("none");
  const [leaveRequestDialogOpen, setLeaveRequestDialogOpen] = useState(false);
  const [leaveRequestForm, setLeaveRequestForm] = useState({
    reason: "",
    start_date: "",
    end_date: "",
  });
  const [isSubmittingLeave, setIsSubmittingLeave] = useState(false);
  const [selectedNotificationTab, setSelectedNotificationTab] =
    useState("pending-requests");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Corporate holidays state
  const [corporateHolidays, setCorporateHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);

  const fetchHeaderProfile = useCallback(async () => {
    try {
      const { data: userProfile } = await api.get(
        `/api/users/me?t=${new Date().getTime()}`
      );
      if (userProfile && typeof userProfile === "object") {
        setProfile(userProfile);
        if (userProfile.name) localStorage.setItem("name", userProfile.name);
        setIsOnLeave(userProfile.leaveStatus || false);
        const photoPath = userProfile.profilePhoto || userProfile.profile_photo;
        if (photoPath) {
          let fullUrl =
            photoPath.startsWith("http://") || photoPath.startsWith("https://")
              ? photoPath
              : `${new URL(api.defaults.baseURL).origin}/${photoPath.replace(
                  /^\/+/,
                  ""
                )}`;
          setProfilePhotoUrl(fullUrl);
        } else {
          setProfilePhotoUrl(defaultAvatar);
        }
      }
    } catch (error) {
      console.error("Error fetching header profile:", error);
      setProfile(null);
    }
  }, []);

  const fetchLeaveRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/api/users/me/my-leave-requests");
      setLeaveRequests(data);
    } catch (error) {
      console.error("Error fetching leave requests:", error);
    }
  }, []);

  const fetchDashboardData = useCallback(
    async (showPageLoader = false) => {
      if (showPageLoader) setIsLoading(true);
      else setIsChartLoading(true);

      const clientToday = formatDateToYMD(new Date());
      const start = formatDateToYMD(chartStartDate);
      const end = formatDateToYMD(chartEndDate);

      try {
        const res = await api.get(
          `/api/users/me/dashboard?startDate=${start}&endDate=${end}&clientToday=${clientToday}`
        );
        // This sets the DYNAMIC data for the chart, calendar, and today's status
        setDashboardData(res.data);
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
        setDashboardData(null);
      } finally {
        if (showPageLoader) setIsLoading(false);
        setIsChartLoading(false);
      }
    },
    [chartStartDate, chartEndDate]
  );

  // <<< STEP 2: MODIFY INITIAL DATA FETCH TO INCLUDE STATIC KPIs >>>
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        await fetchHeaderProfile();
        if (currentSection === "notifications") {
          await fetchLeaveRequests();
        }
        if (["dashboard", "input"].includes(currentSection)) {
          // Fetch the static KPIs for the current month
          const today = new Date();
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          const lastDay = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0
          );
          const kpiRes = await api.get(
            `/api/users/me/dashboard?startDate=${formatDateToYMD(
              firstDay
            )}&endDate=${formatDateToYMD(lastDay)}`
          );
          if (kpiRes.data) {
            setMonthlyKpiData({
              kpis: kpiRes.data.kpis,
              previousMonthKpis: kpiRes.data.previousMonthKpis,
            });
          }

          // Fetch the dynamic data for the chart/calendar (for the initially selected date range)
          await fetchDashboardData(true);
        }
      } catch (e) {
        console.error("Error on initial fetch", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (currentSection === "dashboard") {
      // This correctly refetches only the DYNAMIC data when the chart's date range changes
      fetchDashboardData(false);
    }
  }, [chartStartDate, chartEndDate, currentSection, fetchDashboardData]);

  const handleLeaveRequestDialogClose = () => {
    setLeaveRequestDialogOpen(false);
    setLeaveRequestForm({ reason: "", start_date: "", end_date: "" });
  };

  const handleLeaveRequestSubmit = async () => {
    if (!leaveRequestForm.reason.trim() || !leaveRequestForm.start_date) {
      setSnackbar({
        open: true,
        message: "Reason and start date are required.",
        severity: "warning",
      });
      return;
    }
    setIsSubmittingLeave(true);
    try {
      await api.post("/api/users/me/leave-request", leaveRequestForm);
      setSnackbar({
        open: true,
        message: "Leave request submitted successfully!",
        severity: "success",
      });
      handleLeaveRequestDialogClose();
      fetchLeaveRequests();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Error submitting leave request.",
        severity: "error",
      });
    } finally {
      setIsSubmittingLeave(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  // <<< STEP 3: UPDATE DATA DESTRUCTURING >>>
  // Dynamic data from `dashboardData`
  const { performanceTrend, todaysStatus, submissionHistory } =
    dashboardData || {
      performanceTrend: [],
      todaysStatus: {},
      submissionHistory: {},
    };

  // Static data from our new state
  const { kpis, previousMonthKpis } = monthlyKpiData;

  const generateCalendarDays = () => {
    const daysArray = [];
    if (!calendarStartDate || !calendarEndDate) return [];
    let loopDate = new Date(
      calendarStartDate.getFullYear(),
      calendarStartDate.getMonth(),
      1
    );
    const finalDate = new Date(
      calendarEndDate.getFullYear(),
      calendarEndDate.getMonth() + 1,
      0
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const firstDayOfWeek = loopDate.getDay();
    const blankDays = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    for (let i = 0; i < blankDays; i++) {
      daysArray.push({ key: `blank-${i}` });
    }
    while (loopDate <= finalDate) {
      const dateStr = formatDateToYMD(loopDate);
      const isSunday = loopDate.getDay() === 0;
      const holidayObj = isIndianGovernmentHoliday(loopDate, corporateHolidays);
      const isHoliday = !!holidayObj;

      
      // Use the dynamic `submissionHistory` for the calendar
      let status = submissionHistory[dateStr] || null;
      if (!status && loopDate <= today) {
        if (isHoliday) {
          status = "holiday";
        } else if (isSunday) {
          status = "sunday";
        } else {
          status = "missing";
        }
      } else if (loopDate > today) {
        if (isHoliday) {
          status = "holiday";
        } else {
          status = "future";
        }
      }

      daysArray.push({
        key: dateStr,
        day: loopDate.getDate(),
        status,
        dateStr,
        holidayName: holidayObj?.name,
      });
      loopDate.setDate(loopDate.getDate() + 1);
    }
    return daysArray;
  };

  const calendarDays = generateCalendarDays();
  const monthlyTargetPoints = 1000;

  // Use the static `kpis` for this calculation
  const monthlyTargetProgress =
    kpis.aggregatedPoints > 0
      ? Math.round((kpis.aggregatedPoints / monthlyTargetPoints) * 100)
      : 0;

  const dashboardTitle = `${toTitleCase(userRole)} Dashboard`;
  const userRoleDisplay = toTitleCase(userRole);
  const chartFieldOptions = [
    { value: "dailyPoints", label: "Daily Points" },
    { value: "telephoneSalesCalls", label: "Telephone Sales Calls" },
    { value: "faceToFace", label: "Face to Face" },
    { value: "appointmentMade", label: "Appointment Made" },
    { value: "appointmentKept", label: "Appointment Kept" },
    { value: "factFind", label: "Fact Find" },
    { value: "serviceReviews", label: "Service Reviews" },
    { value: "socialCalls", label: "Social Calls" },
    { value: "highIncomeWealthy", label: "High Income / Wealthy" },
    { value: "referrals", label: "Referrals" },
    { value: "closingInterview", label: "Closing Interview" },
    { value: "salesClose", label: "Sales Close" },
    { value: "none", label: "None" },
  ];

  return (
    <div>
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo-container">
              <div className="logo">
                <img src={logo1} alt="Logo" />
              </div>
              <span className="logo-text">Aionion Capital</span>
            </div>
            <div className="header-title-group">
              <h1>{dashboardTitle}</h1>
              <span className="performance-tag">Performance Tracking</span>
            </div>
          </div>
          <div className="header-right">
            <nav className="nav-tabs">
              <button
                className={`nav-tab ${
                  currentSection === "dashboard" ? "active" : ""
                }`}
                onClick={() =>
                  navigate("/relationship-manager-dashboard/dashboard")
                }
              >
                <FiBarChart2 /> Dashboard
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "input" ? "active" : ""
                }`}
                onClick={() =>
                  navigate("/relationship-manager-dashboard/input")
                }
              >
                <FiPlus /> Input
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "monthly-report" ? "active" : ""
                }`}
                onClick={() =>
                  navigate("/relationship-manager-dashboard/monthly-report")
                }
              >
                <FiFileText /> Monthly Report
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "settings" ? "active" : ""
                }`}
                onClick={() =>
                  navigate("/relationship-manager-dashboard/settings")
                }
              >
                <FiSettings /> Settings
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "notifications" ? "active" : ""
                }`}
                onClick={() =>
                  navigate("/relationship-manager-dashboard/notifications")
                }
              >
                <FiBell /> Notifications
              </button>
            </nav>
            <div className="user-profile">
              <img
                src={profilePhotoUrl}
                alt="Profile"
                className="profile-picture"
                onError={(e) => {
                  e.target.src = defaultAvatar;
                }}
              />
              <div className="user-info">
                <span className="user-name">{profile?.name || userName}</span>
                <span className="user-role">{userRoleDisplay}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="main-content">
        {/* <<< STEP 4: UPDATE JSX TO USE THE CORRECT DATA SOURCES >>> */}
        {currentSection === "dashboard" && dashboardData && (
          <div className="dashboard-grid">
            <section className="card todays-status-card">
              <div className="card-header">
                <h2>Today's Status</h2>
                <div className="live-indicator">
                  {(() => {
                    const date = new Date();
                    const day = date.getDate();
                    const month = date.toLocaleString("en-GB", {
                      month: "short",
                    });
                    const year = date.getFullYear();
                    return `${day} ${month},${year}`;
                  })()}
                </div>
              </div>
              <div className="status-boxes">
                {/* This part remains DYNAMIC, using `todaysStatus` */}
                {todaysStatus.submitted ? (
                  <div className="status-box submitted">
                    <div className="status-content">
                      <h3>Submitted</h3>
                      <p>Entry for today is complete</p>
                      <span>
                        <FiClock /> Last updated at{" "}
                        {todaysStatus.submissionTime &&
                          new Date(
                            todaysStatus.submissionTime
                          ).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                      </span>
                    </div>
                    <div className="status-icon-wrapper green">
                      <FiCheckCircle />
                    </div>
                  </div>
                ) : (
                  <div className="status-box not-submitted">
                    <div className="status-content">
                      <h3>Not Submitted</h3>
                      <p>No entry for today yet</p>
                      <span>Click Quick Entry to submit.</span>
                    </div>
                    <div className="status-icon-wrapper grey">
                      <FiClock />
                    </div>
                  </div>
                )}
                {/* This "Quick Stats" part becomes STATIC, using `kpis` from `monthlyKpiData` */}
                <Tooltip
                  title={
                    <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
                      <div>
                        Avg Daily Calls Made: {kpis.avgDailyCalls || "0.0"}
                      </div>
                      <div>Avg Sales Closed: {kpis.avgSalesClose || "0.0"}</div>
                      <div>
                        Total Appointments Kept: {kpis.appointmentsKept || 0}
                      </div>
                    </div>
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ffffff",
                        color: "#000000",
                        fontSize: "14px",
                        padding: "14px 18px",
                        borderRadius: "10px",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
                        minWidth: "220px",
                        lineHeight: "1.6",
                      },
                    },
                    arrow: { sx: { color: "#ffffff" } },
                  }}
                >
                  <div className="status-box quick-stats">
                    <div className="status-content">
                      <h3>Quick Stats</h3>
                      <p>This month's summary</p>
                      <span>
                        <FiTrendingUp /> Avg daily points:{" "}
                        {kpis &&
                          Math.round(
                            (kpis.aggregatedPoints || 0) /
                              new Date(
                                new Date().getFullYear(),
                                new Date().getMonth() + 1,
                                0
                              ).getDate()
                          )}
                      </span>
                      <span>
                        <FiAward /> Top activity:{" "}
                        {(() => {
                          const activities = {
                            "Telephone Sales Calls":
                              kpis.telephoneSalesCalls || 0,
                            "Face To Face": kpis.faceToFace || 0,
                            "Appointments Kept": kpis.appointmentsKept || 0,
                            "Sales Close": kpis.salesClose || 0,
                          };
                          const maxValue = Math.max(
                            ...Object.values(activities)
                          );
                          if (maxValue === 0) return "No Activity";
                          const topActivity = Object.keys(activities).reduce(
                            (a, b) => (activities[a] > activities[b] ? a : b)
                          );
                          return topActivity;
                        })()}
                      </span>
                    </div>
                    <div className="status-icon-wrapper blue">
                      <FiTrendingUp />
                    </div>
                  </div>
                </Tooltip>
              </div>
            </section>

            <section
              className={`card quick-entry-card ${
                todaysStatus?.submitted ? "editable" : ""
              } ${isOnLeave ? "disabled" : ""}`}
            >
              <div
                className="quick-entry-icon"
                onClick={
                  isOnLeave
                    ? undefined
                    : () => navigate("/relationship-manager-dashboard/input")
                }
              >
                <FiPlus />
              </div>
              <h2>
                {todaysStatus?.submitted ? "Edit Today's Entry" : "Quick Entry"}
              </h2>
              <button
                className="submit-entry-btn"
                onClick={
                  isOnLeave
                    ? undefined
                    : () => navigate("/relationship-manager-dashboard/input")
                }
                disabled={isOnLeave}
              >
                {todaysStatus?.submitted
                  ? "Edit Entry"
                  : "Submit Today's Entry"}
              </button>
              <span className="secure-note">
                <FiLock /> Secure & Encrypted
              </span>
            </section>

            {/* The rest of the KPI cards are now correctly using the STATIC `kpis` */}
            <div
              className="kpi-cards-container"
              style={{ gridColumn: "span 4" }}
            >
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Aggregated Points</h4>
                  <FiAward className="stat-icon trophy" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {kpis.aggregatedPoints || 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month: {previousMonthKpis.aggregatedPoints || 0}
                </p>
              </section>

              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Monthly Target</h4>
                  <FiTarget className="stat-icon target" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">{monthlyTargetProgress}%</span>
                </div>
                <p className="stat-period">
                  Current: {kpis.aggregatedPoints || 0} / {monthlyTargetPoints}
                </p>
              </section>

              <section className="card stat-card">
                <div className="stat-header">
                  <h4>New Sales Calls</h4>
                  <FiUsers className="stat-icon interactions" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {kpis.salesInteractions || 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month: {previousMonthKpis.salesInteractions || 0}
                </p>
              </section>

              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Avg Daily Calls</h4>
                  <FiPhoneCall className="stat-icon target" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {kpis.avgDailyCalls || "0.0"}
                  </span>
                </div>
                <p className="stat-period">
                  Last month: {previousMonthKpis.avgDailyCalls || "0.0"}
                </p>
              </section>

              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Avg Sales Close</h4>
                  <FiTrendingUp className="stat-icon conversion" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {kpis.avgSalesClose || "0.0"}
                  </span>
                </div>
                <p className="stat-period">
                  Last month: {previousMonthKpis.avgSalesClose || "0.0"}
                </p>
              </section>

              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Appts. Kept</h4>
                  <FiCheckSquare className="stat-icon interactions" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {kpis.appointmentsKept || 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month: {previousMonthKpis.appointmentsKept || 0}
                </p>
              </section>

              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Conversion Rate</h4>
                  <FiBarChart2 className="stat-icon conversion" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {kpis.conversionRate || 0}%
                  </span>
                </div>
                <p className="stat-period">
                  Last month: {previousMonthKpis.conversionRate || 0}%
                </p>
              </section>
            </div>

            {/* The submission history and performance trends cards are unchanged and will remain dynamic. */}
            
            <section
              className="card submission-history-card"
              style={{ position: "relative" }}
            >
              {isChartLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(var(--card-bg-rgb), 0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                    borderRadius: "var(--border-radius)",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              <div className="card-header">
                <h2>Submission History</h2>
                <div className="date-range-filters">
                  <div className="date-input-group">
                    <label htmlFor="calendar-start-date">From</label>
                    <input
                      type="date"
                      id="calendar-start-date"
                      value={formatDateToYMD(calendarStartDate)}
                      onChange={(e) =>
                        setCalendarStartDate(
                          new Date(e.target.value.replace(/-/g, "/"))
                        )
                      }
                    />
                  </div>
                  <div className="date-input-group">
                    <label htmlFor="calendar-end-date">To</label>
                    <input
                      type="date"
                      id="calendar-end-date"
                      value={formatDateToYMD(calendarEndDate)}
                      onChange={(e) =>
                        setCalendarEndDate(
                          new Date(e.target.value.replace(/-/g, "/"))
                        )
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="calendar-grid-wrapper">
                <div className="calendar-sticky-header">
                  {["M", "T", "W", "T", "F", "S", "S"].map((day, index) => (
                    <div key={index} className="calendar-day-header">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="calendar-grid">
                  {calendarDays.map((dayInfo) => {
                    if (!dayInfo.day)
                      return (
                        <div key={dayInfo.key} className="calendar-day"></div>
                      );
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const [year, month, day] = dayInfo.dateStr
                      .split("-")
                      .map(Number);
                    const entryDateObj = new Date(year, month - 1, day);
                    const daysDiff = Math.floor(
                      (today - entryDateObj) / (1000 * 60 * 60 * 24)
                    );
                    const isClickable = entryDateObj <= today;
                    const isEditable = daysDiff >= 0 && daysDiff <= 2;
                    let className = `calendar-day ${dayInfo.status || ""}`;
                    if (isClickable) className += " clickable";
                    if (dayInfo.dateStr === dateFromUrl)
                      className += " selected";
                    return (
                      <div
                        key={dayInfo.key}
                        className={className}
                        onClick={() =>
                          isClickable &&
                          navigate(
                            `/relationship-manager-dashboard/input/${dayInfo.dateStr}`
                          )
                        }
                      >
                        {dayInfo.status === "submitted" && <FiCheckCircle />}
                        {dayInfo.status === "missing" && (
                          <span className="missing-icon">&times;</span>
                        )}
                        {dayInfo.status === "holiday" && (
                          <span className="holiday-icon">H</span>
                        )}
                        {dayInfo.status && !isEditable && daysDiff > 2 && (
                          <FiLock className="lock-icon" />
                        )}
                        <span>{dayInfo.day}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="calendar-legend">
                <div className="legend-item">
                  <span className="legend-dot submitted-dot"></span> Submitted
                </div>
                <div className="legend-item">
                  <span className="legend-dot missing-dot"></span> Missing
                </div>
                <div className="legend-item">
                  <span className="legend-dot sunday"></span> Sunday
                </div>
                <div className="legend-item">
                  <FiLock style={{ marginRight: "4px" }} /> View Only
                </div>
              </div>
            </section>

            <section
              className="card performance-trends-card"
              style={{ position: "relative" }}
            >
              {isChartLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(var(--card-bg-rgb), 0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                    borderRadius: "var(--border-radius)",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              <div className="card-header">
                <h2>Performance Trends</h2>
                <div className="date-range-filters">
                  <div className="date-input-group">
                    <label htmlFor="chart-start-date">From</label>
                    <input
                      type="date"
                      id="chart-start-date"
                      value={formatDateToYMD(chartStartDate)}
                      onChange={(e) =>
                        setChartStartDate(
                          new Date(e.target.value.replace(/-/g, "/"))
                        )
                      }
                    />
                  </div>
                  <div className="date-input-group">
                    <label htmlFor="chart-end-date">To</label>
                    <input
                      type="date"
                      id="chart-end-date"
                      value={formatDateToYMD(chartEndDate)}
                      onChange={(e) =>
                        setChartEndDate(
                          new Date(e.target.value.replace(/-/g, "/"))
                        )
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="field-filters">
                <div className="field-filter">
                  <label htmlFor="field-select1">Metric 1:</label>
                  <select
                    id="field-select1"
                    value={selectedField1}
                    onChange={(e) => setSelectedField1(e.target.value)}
                    className="field-select"
                  >
                    {chartFieldOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field-filter">
                  <label htmlFor="field-select2">Metric 2:</label>
                  <select
                    id="field-select2"
                    value={selectedField2}
                    onChange={(e) => setSelectedField2(e.target.value)}
                    className="field-select"
                  >
                    {chartFieldOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="chart-container">
                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-dot blue-dot"></span>{" "}
                    {toTitleCase(selectedField1)}
                  </div>
                  {selectedField2 !== "none" && (
                    <div className="legend-item">
                      <span className="legend-dot green-dot"></span>{" "}
                      {toTitleCase(selectedField2)}
                    </div>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={performanceTrend}
                    margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                    {selectedField2 !== "none" && (
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 10 }}
                      />
                    )}
                    <ChartTooltip
                      wrapperStyle={{ zIndex: 9999 }}
                      contentStyle={{
                        backgroundColor: "var(--card-bg, #ffffff)",
                        border: "1px solid var(--border-color, #e0e0e0)",
                        borderRadius: "8px",
                        boxShadow: "var(--card-shadow)",
                      }}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey={selectedField1}
                      stroke="#4A90E2"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name={toTitleCase(selectedField1)}
                    />
                    {selectedField2 !== "none" && (
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey={selectedField2}
                        stroke="#28a745"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        name={toTitleCase(selectedField2)}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          </div>
        )}

        {currentSection === "input" && (
          <SalesTracker
            dashboardData={dashboardData}
            onBackClick={() =>
              navigate("/relationship-manager-dashboard/dashboard")
            }
            onEntrySubmit={() => fetchDashboardData(false)}
            entryDate={dateFromUrl || formatDateToYMD(new Date())}
            isOnLeave={isOnLeave}
            isEditable={(() => {
              const entryDate = dateFromUrl
                ? new Date(dateFromUrl.replace(/-/g, "/"))
                : new Date();
              const today = new Date();
              entryDate.setHours(0, 0, 0, 0);
              today.setHours(0, 0, 0, 0);
              const daysDiff = Math.floor(
                (today - entryDate) / (1000 * 60 * 60 * 24)
              );
              return daysDiff >= 0 && daysDiff <= 2;
            })()}
          />
        )}
        {currentSection === "monthly-report" && <MonthlyReport />}
        {currentSection === "settings" && profile && (
          <ProfilePage
            profile={profile}
            profilePhotoUrl={profilePhotoUrl}
            onProfileUpdate={fetchHeaderProfile}
          />
        )}
        {/* The rest of the component remains the same */}
        {currentSection === "notifications" && (
          <div className="notifications-section">
            <div className="notification-tabs">
              <button
                className={`notification-tab ${
                  selectedNotificationTab === "pending-requests" ? "active" : ""
                }`}
                onClick={() => setSelectedNotificationTab("pending-requests")}
              >
                Pending Requests
              </button>
              <button
                className={`notification-tab ${
                  selectedNotificationTab === "approved-rejected"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("approved-rejected")}
              >
                Notifications
              </button>
            </div>
            <div className="notification-content">
              {selectedNotificationTab === "pending-requests" && (
                <section>
                  <h3>Pending Leave Requests</h3>
                  {leaveRequests.filter(
                    (request) => request.status === "pending"
                  ).length > 0 ? (
                    <ul className="notifications-list">
                      {leaveRequests
                        .filter((request) => request.status === "pending")
                        .map((request, index) => (
                          <li key={index} className="notification-item">
                            <div className="notification-details">
                              <p>
                                <strong>Reason:</strong> {request.reason}
                              </p>
                              <p>
                                <strong>Start Date:</strong>{" "}
                                {formatDateToYMD(
                                  new Date(
                                    request.startDate || request.start_date
                                  )
                                )}
                              </p>
                              <p>
                                <strong>End Date:</strong>{" "}
                                {request.endDate || request.end_date
                                  ? formatDateToYMD(
                                      new Date(
                                        request.endDate || request.end_date
                                      )
                                    )
                                  : "N/A"}
                              </p>
                            </div>
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p className="no-notifications">
                      No pending leave requests.
                    </p>
                  )}
                </section>
              )}
              {selectedNotificationTab === "approved-rejected" && (
                <section>
                  <h3>Leave Notifications</h3>
                  {leaveRequests.filter(
                    (request) =>
                      request.status === "approved" ||
                      request.status === "rejected"
                  ).length > 0 ? (
                    <ul className="notifications-list">
                      {leaveRequests
                        .filter(
                          (request) =>
                            request.status === "approved" ||
                            request.status === "rejected"
                        )
                        .map((request, index) => (
                          <li key={index} className="notification-item">
                            <Typography>
                              Your leave request from{" "}
                              <strong>
                                {formatDateToYMD(
                                  new Date(
                                    request.startDate || request.start_date
                                  )
                                )}
                              </strong>{" "}
                              to{" "}
                              <strong>
                                {request.endDate || request.end_date
                                  ? formatDateToYMD(
                                      new Date(
                                        request.endDate || request.end_date
                                      )
                                    )
                                  : "N/A"}
                              </strong>{" "}
                              has been{" "}
                              <strong
                                style={{
                                  color:
                                    request.status === "approved"
                                      ? "var(--green)"
                                      : "var(--pink)",
                                }}
                              >
                                {request.status}
                              </strong>
                              {request.approvedBy && request.approverRole && (
                                <>
                                  {" by "}
                                  <strong>{request.approvedBy}</strong>
                                  {" ("}
                                  <strong>
                                    {toTitleCase(request.approverRole)}
                                  </strong>
                                  {")"}
                                </>
                              )}
                              .
                            </Typography>
                            {request.rejectionReason && (
                              <Typography
                                variant="body2"
                                sx={{ mt: 1, color: "text.secondary" }}
                              >
                                <strong>Reason:</strong>{" "}
                                {request.rejectionReason}
                              </Typography>
                            )}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p className="no-notifications">No leave notifications.</p>
                  )}
                </section>
              )}
            </div>
          </div>
        )}
      </main>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={leaveRequestDialogOpen}
        onClose={handleLeaveRequestDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit Leave Request</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason"
            value={leaveRequestForm.reason}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                reason: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            multiline
            rows={3}
          />
          <TextField
            label="Start Date"
            type="date"
            value={leaveRequestForm.start_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                start_date: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={leaveRequestForm.end_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                end_date: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLeaveRequestDialogClose}>Cancel</Button>
          <Button
            onClick={handleLeaveRequestSubmit}
            variant="contained"
            disabled={isSubmittingLeave}
          >
            {isSubmittingLeave ? <CircularProgress size={24} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RMDashboard;
